import { ApiService } from "@core/api/api-service";
// ProgramId import removed - now using string type instead
import { DayIndex } from "@domain/program/program-days";
import { Exertion, Mood } from "@domain/progress/patient-feedback";
import { ComplianceDay, ComplianceGraphData, FeedbackGraphData, ProgressData } from "@domain/progress/progress-data";
import { dateFromDateDto } from "@utils/date-utils";

export class ProgressDataService {
	constructor(private readonly apiService: ApiService) {}

	async loadProgressData(patientId: string, programId: string): Promise<ProgressData> {
		const result = await this.apiService.get<ProgressDataDto>(`/patients/${patientId}/programs/${programId}/kpis`);
		return ProgressDataService.progressDataFromDto(result.data);
	}

	async loadComplianceGraphData(patientId: string, programId: string): Promise<ComplianceGraphData> {
		const result = await this.apiService.get<ComplianceGraphDataDto>(
			`/patients/${patientId}/programs/${programId}/kpis/weekly-compliance`,
		);
		return ProgressDataService.complianceGraphDataFromDto(result.data);
	}

	async loadFeedbackGraphData(patientId: string, programId: string): Promise<FeedbackGraphData> {
		const result = await this.apiService.get<FeedbackGraphDataDto>(
			`/patients/${patientId}/programs/${programId}/kpis/daily-feedback`,
		);
		return ProgressDataService.feedbackGraphDataFromDto(result.data);
	}

	// Dto Conversions
	private static progressDataFromDto(progressDataDto: ProgressDataDto): ProgressData {
		return {
			...progressDataDto,
			startDate: dateFromDateDto(progressDataDto.startDate),
			endDate: dateFromDateDto(progressDataDto.endDate),
			lastCompletedSession: progressDataDto.lastCompletedSession
				? dateFromDateDto(progressDataDto.lastCompletedSession)
				: undefined,
			programProgressInPercent: progressDataDto.totalSessions
				? (progressDataDto.pastSessionsCount / progressDataDto.totalSessions) * 100
				: 0,
			programComplianceInPercent: progressDataDto.pastSessionsCount
				? (progressDataDto.completedSessionsCount / progressDataDto.pastSessionsCount) * 100
				: 0,
		};
	}

	private static complianceGraphDataFromDto(complianceGraphDataDto: ComplianceGraphDataDto): ComplianceGraphData {
		return complianceGraphDataDto.map(({ startDate, endDate, days, ...otherAttributes }) => ({
			...otherAttributes,
			days: days.map(dayDto => this.complianceDayFromDto(dayDto)),
			startDate: dateFromDateDto(startDate),
			endDate: dateFromDateDto(endDate),
		}));
	}

	private static complianceDayFromDto(dayDto: ComplianceDayDto): ComplianceDay {
		return { day: DayIndex[dayDto.day as keyof typeof DayIndex], completed: dayDto.completed };
	}

	private static feedbackGraphDataFromDto(feedbackGraphDataDto: FeedbackGraphDataDto): FeedbackGraphData {
		return {
			feedbacks: feedbackGraphDataDto.feedbacks.map((feedbackPerSessionDto, i) => ({
				...feedbackPerSessionDto,
				index: i + 1,
				completionDate: dateFromDateDto(feedbackPerSessionDto.completionDate),
			})),
		};
	}
}

interface ProgressDataDto {
	sessionsPerWeek: number;
	exercisesPerWeek: number;
	startDate: string;
	endDate: string;
	lastCompletedSession?: string;
	completedSessionsCount: number;
	pastSessionsCount: number;
	totalSessions: number;
}

interface CompliancePerWeekDto {
	weekIndex: number;
	startDate: string;
	endDate: string;
	compliancePercentage: number;
	days: ComplianceDayDto[];
}

interface ComplianceDayDto {
	day: string;
	completed: boolean;
}

type ComplianceGraphDataDto = CompliancePerWeekDto[];

interface FeedbackPerSessionDto {
	completionDate: string;
	exertion: Exertion | null;
	mood: Mood | null;
}

interface FeedbackGraphDataDto {
	feedbacks: FeedbackPerSessionDto[];
}
